import { useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, NotebookPen, Flame, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n.jsx";

/**
 * SharedCalendarCard — read-only trainee calendar for the coach share view.
 *
 * Renders a compact monthly grid with dots under each day (mirroring the
 * regular CalendarPage colour code):
 *   • green  = every task done that day
 *   • orange = at least one task pending
 *   • blue   = journal-only entry, no tasks
 *
 * Tap a day → expand a tiny inline preview underneath the grid showing
 * the day's task titles + journal excerpt. Nothing here is editable —
 * the coach can only look.
 *
 * Data source: the same `/api/notes/range` endpoint used by the owner's
 * CalendarPage. The share-token JWT resolves to the owner so this works
 * transparently — we don't need a dedicated share endpoint.
 */
const AR_MONTHS = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
const EN_MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const AR_DAYS = ["أحد","اثنين","ثلاثاء","أربعاء","خميس","جمعة","سبت"];
const EN_DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayY = () => ymd(new Date());

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const cells = [];
  const startCell = new Date(year, month, 1 - startDow);
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(startCell);
    d.setDate(startCell.getDate() + i);
    cells.push(d);
  }
  return cells;
}

// Reduce each day payload into the dot colour we render below.
// Priority: pending > all-done > journal > none. Kept as a switch
// statement so the intent is obvious — matches the CalendarPage rule.
function dayDotClass(day) {
  if (!day) return "";
  const tasks = day.tasks || [];
  const hasJournal = !!(day.journal && (day.journal.body || day.journal.body_html));
  if (tasks.length > 0) {
    const pending = tasks.some((t) => !t.done);
    return pending ? "bg-orange-500" : "bg-emerald-500";
  }
  return hasJournal ? "bg-sky-500" : "";
}

export default function SharedCalendarCard() {
  const { lang } = useI18n();
  const en = lang === "en";
  const dir = en ? "ltr" : "rtl";
  const T = en ? {
    title: "Trainee's calendar",
    subtitle: "Tasks + journal — read-only snapshot",
    loading: "Loading calendar…",
    tapHint: "Tap a day to jump",
    empty: "No tasks or journal entries this month",
    donePill: "Done",
    pendingPill: "Pending",
    journal: "Journal",
    prev: "Previous month",
    next: "Next month",
    monthTasks: "tasks",
    monthDone: "done",
    monthJournals: "journal days",
    fullMonth: (m) => `Everything in ${m}`,
  } : {
    title: "تقويم المتدرّب",
    subtitle: "المهام + اليوميات — عرض للاطلاع فقط",
    loading: "جارٍ تحميل التقويم…",
    tapHint: "اضغط على يوم للوصول السريع",
    empty: "لا توجد مهام أو يوميات في هذا الشهر",
    donePill: "تم",
    pendingPill: "قيد التنفيذ",
    journal: "اليوميات",
    prev: "الشهر السابق",
    next: "الشهر التالي",
    monthTasks: "مهمة",
    monthDone: "منجزة",
    monthJournals: "أيام يوميات",
    fullMonth: (m) => `كل ما في ${m}`,
  };
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const monthName = (en ? EN_MONTHS : AR_MONTHS)[cursor.m];
  const dayHeaders = en ? EN_DAYS : AR_DAYS;
  const grid = useMemo(() => buildMonthGrid(cursor.y, cursor.m), [cursor]);

  useEffect(() => {
    setLoading(true);
    setSelected(null);
    const start = ymd(grid[0]);
    const end = ymd(grid[grid.length - 1]);
    // Use the coach-view full range endpoint so we get task titles +
    // journal body, not just per-day counts. `/notes/range` (used by
    // the owner's CalendarPage) is a summary-only endpoint and would
    // leave the flat list empty.
    api.get(`/notes/range-full?start=${start}&end=${end}`)
      .then((r) => {
        const map = {};
        (r.data?.days || []).forEach((d) => { map[d.date] = d; });
        setDays(map);
      })
      .catch(() => setDays({}))
      .finally(() => setLoading(false));
  }, [grid]);

  const selDay = selected ? days[selected] : null;
  const isTodayCell = (d) => ymd(d) === todayY();
  const inMonth = (d) => d.getMonth() === cursor.m;

  // Flatten every non-empty day of the ACTIVE month into a chronological
  // list — coaches need to see the whole month's plan at a glance, not
  // hunt-and-tap. Sorted newest-first so recent activity floats to top.
  // (Filter to `inMonth` so leading/trailing padding cells from the grid
  // don't leak entries that belong to the neighbouring months.)
  const monthEntries = useMemo(() => {
    return Object.entries(days)
      .filter(([date]) => {
        const [y, m] = date.split("-").map(Number);
        return y === cursor.y && (m - 1) === cursor.m;
      })
      .filter(([, day]) => (day?.tasks || []).length > 0 || (day?.journal && (day.journal.body || day.journal.body_html)))
      .sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [days, cursor]);

  const monthLabel = `${monthName} ${cursor.y}`;
  const totalTasks = monthEntries.reduce((s, [, d]) => s + (d.tasks?.length || 0), 0);
  const doneTasks  = monthEntries.reduce((s, [, d]) => s + (d.tasks?.filter((t) => t.done).length || 0), 0);
  const journalDays = monthEntries.filter(([, d]) => d.journal && (d.journal.body || d.journal.body_html)).length;

  // Weekday label for a given ISO date — used in the flat list header.
  const weekdayLabel = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    const dow = new Date(y, m - 1, d).getDay();
    return dayHeaders[dow];
  };

  return (
    <div className="px-5 mb-4" dir={dir}>
      <div className="bg-white rounded-3xl shadow-card overflow-hidden border-[1.5px] border-black">
        {/* Header — matches TraineeProfileCard tone */}
        <div className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-br from-sky-50 to-indigo-50">
          <div className="w-10 h-10 rounded-2xl bg-sky-500 text-white flex items-center justify-center shadow-sm">
            <CalendarDays className="w-5 h-5" strokeWidth={2.2} />
          </div>
          <div className={`flex-1 ${en ? "text-left" : "text-right"}`}>
            <div className="text-sm font-black text-neutral-900">{T.title}</div>
            <div className="text-[11px] text-neutral-600">{T.subtitle}</div>
          </div>
        </div>

        {/* Month navigation */}
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setCursor(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }))}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-neutral-100 active:scale-90"
            aria-label={T.prev}
            data-testid="shared-cal-prev"
          >
            {en ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div className="text-sm font-black num" dir="ltr">
            {monthName} {cursor.y}
          </div>
          <button
            onClick={() => setCursor(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }))}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-neutral-100 active:scale-90"
            aria-label={T.next}
            data-testid="shared-cal-next"
          >
            {en ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* Weekday header row */}
        <div className="grid grid-cols-7 gap-1 px-4 pb-1">
          {dayHeaders.map((d) => (
            <div key={d} className="text-[10px] font-semibold text-neutral-400 text-center">{d}</div>
          ))}
        </div>

        {/* Month grid with dots */}
        {loading ? (
          <div className="py-12 flex justify-center items-center gap-2 text-neutral-400">
            <Loader2 className="w-4 h-4 animate-spin" /> <span className="text-xs">{T.loading}</span>
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1 px-4 pb-3">
            {grid.map((d, i) => {
              const key = ymd(d);
              const dot = dayDotClass(days[key]);
              const cellSel = selected === key;
              return (
                <button
                  key={i}
                  onClick={() => {
                    setSelected(key);
                    // Scroll the flat list below to the tapped day so the
                    // coach jumps straight to the details without hunting.
                    setTimeout(() => {
                      const el = document.getElementById(`day-${key}`);
                      if (el && typeof el.scrollIntoView === "function") {
                        el.scrollIntoView({ block: "center", behavior: "smooth" });
                      }
                    }, 30);
                  }}
                  className={`relative aspect-square rounded-xl text-xs font-bold transition-all active:scale-95
                    ${cellSel ? "bg-brand-500 text-white shadow-md" :
                      !inMonth(d) ? "text-neutral-300" :
                      isTodayCell(d) ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200" :
                      "text-neutral-700 hover:bg-neutral-50"}
                  `}
                  data-testid={`shared-cal-day-${key}`}
                >
                  <span className="num" dir="ltr">{d.getDate()}</span>
                  {dot && !cellSel && (
                    <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${dot}`} />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Selected day preview — read-only. Highlights on-tap but ALSO
            scrolls the flat list below to the day (see #day-{date}). */}
        {/* Month summary chip row — visible before the list so a coach can
            grasp the month's shape in ~1 second. */}
        {!loading && (
          <div className="px-4 pt-2 pb-1 flex gap-1.5 flex-wrap text-[10px] font-bold">
            <span className="px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 num" dir="ltr">
              {totalTasks} {T.monthTasks}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 num" dir="ltr">
              {doneTasks} {T.monthDone}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 num" dir="ltr">
              {journalDays} {T.monthJournals}
            </span>
          </div>
        )}

        {/* Flat, always-visible chronological list of every non-empty day
            in the SELECTED month. Coaches can scan the entire month
            without tapping around — matches how they'd review a coaching
            log in real life. Empty months show a single "empty" line. */}
        {!loading && (
          <div className="border-t border-neutral-100 bg-neutral-50 px-4 py-3 space-y-3 max-h-[420px] overflow-y-auto">
            <div className="text-[10px] font-black text-neutral-500 uppercase tracking-wide" dir={dir}>
              {T.fullMonth(monthLabel)}
            </div>
            {monthEntries.length === 0 ? (
              <div className="text-[11px] text-neutral-400 italic py-4 text-center">{T.empty}</div>
            ) : (
              monthEntries.map(([date, day]) => (
                <div key={date} id={`day-${date}`} className={`bg-white rounded-2xl p-3 border ${selected === date ? "border-brand-400 ring-1 ring-brand-200" : "border-neutral-100"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-xl bg-brand-50 text-brand-700 flex flex-col items-center justify-center leading-none">
                        <span className="text-[9px] font-bold opacity-60">{weekdayLabel(date)}</span>
                        <span className="text-sm font-black num" dir="ltr">{Number(date.split("-")[2])}</span>
                      </div>
                      <div className="text-[10px] text-neutral-500 num" dir="ltr">{date}</div>
                    </div>
                    {day.streak_day && (
                      <span className="flex items-center gap-1 text-orange-600 text-[10px] font-bold">
                        <Flame className="w-3 h-3" /> <span className="num" dir="ltr">{day.streak_day}</span>
                      </span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {(day.tasks || []).map((t) => (
                      <div key={t.id} className="flex items-start gap-2">
                        <div className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black flex-shrink-0
                          ${t.done ? "bg-emerald-500 text-white" : "bg-amber-100 text-amber-700 border border-amber-300"}`}>
                          {t.done ? "✓" : "!"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-[12px] font-semibold text-neutral-800 ${t.done ? "line-through text-neutral-500" : ""}`}>
                            {t.title}
                          </div>
                        </div>
                      </div>
                    ))}
                    {day.journal && (day.journal.body || day.journal.body_html) && (
                      <div className="mt-1 rounded-xl px-2 py-1.5 bg-sky-50/70">
                        <div className="text-[10px] font-black text-sky-700 mb-0.5 flex items-center gap-1">
                          <NotebookPen className="w-3 h-3" /> {T.journal}
                        </div>
                        <div className="text-[11px] text-neutral-700 leading-relaxed whitespace-pre-wrap">
                          {day.journal.body || (day.journal.body_html || "").replace(/<[^>]+>/g, "")}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
